import { useEffect, useState, useCallback } from "react";
import { Vote, Plus, X, Lock, Trash2, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Trip Polls — group decisions on a shared trip (GroupTrip Planner).
   Everyone on the trip votes; owner/editors create; author/owner closes. */

export default function TripPolls({ tripId, canCreate, isOwner }) {
  const [polls, setPolls] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get(`/trips/${tripId}/polls`).then((r) => setPolls(r.data.items)).catch(() => setPolls([]));
  }, [tripId]);
  useEffect(() => { load(); }, [load]);

  if (!polls) return null;
  if (polls.length === 0 && !canCreate) return null;

  return (
    <div id="trip-polls" className="mb-10 border border-violet-300 bg-violet-50/50 scroll-mt-24" data-testid="trip-polls">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-violet-200">
        <Vote className="w-5 h-5 text-violet-700" />
        <div className="font-heading font-900 text-violet-900">Group Polls</div>
        <span className="text-xs text-muted-foreground">— let everyone on the trip vote</span>
        {canCreate && (
          <button onClick={() => setCreating((c) => !c)} data-testid="poll-new-btn"
            className="ml-auto inline-flex items-center gap-1 px-3 py-1.5 bg-violet-700 text-white text-xs font-700 hover:bg-violet-600 transition-colors">
            {creating ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />} {creating ? "Cancel" : "New poll"}
          </button>
        )}
      </div>
      <div className="p-4 space-y-4">
        {creating && <PollForm tripId={tripId} onDone={() => { setCreating(false); load(); }} />}
        {polls.length === 0 && !creating && (
          <p className="text-sm text-muted-foreground">No polls yet. Ask the group something — beach day or backwaters?</p>
        )}
        {polls.map((p) => <PollCard key={p.id} tripId={tripId} poll={p} isOwner={isOwner} onChange={load} />)}
      </div>
    </div>
  );
}

function PollForm({ tripId, onDone }) {
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);
  const [closesAt, setClosesAt] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (!question.trim() || opts.length < 2) { toast.error("Add a question and at least 2 options"); return; }
    setBusy(true);
    try {
      const payload = { question: question.trim(), options: opts };
      if (closesAt) payload.closes_at = new Date(closesAt).toISOString();
      await api.post(`/trips/${tripId}/polls`, payload);
      toast.success("Poll posted — members get a notification");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create poll"); }
    setBusy(false);
  };
  return (
    <div className="border border-violet-300 bg-white p-4" data-testid="poll-form">
      <input value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={200}
        placeholder="Question — e.g. Which day for the boat trip?" data-testid="poll-question-input"
        className="w-full border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:border-violet-500" />
      <div className="mt-2 space-y-1.5">
        {options.map((o, i) => (
          <div key={i} className="flex gap-1.5">
            <input value={o} onChange={(e) => setOptions((os) => os.map((x, j) => (j === i ? e.target.value : x)))}
              maxLength={120} placeholder={`Option ${i + 1}`} data-testid={`poll-option-input-${i}`}
              className="flex-1 border border-zinc-300 px-3 py-1.5 text-sm focus:outline-none focus:border-violet-500" />
            {options.length > 2 && (
              <button onClick={() => setOptions((os) => os.filter((_, j) => j !== i))} className="px-2 text-zinc-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {options.length < 8 && (
          <button onClick={() => setOptions((os) => [...os, ""])} data-testid="poll-add-option"
            className="text-xs font-700 text-violet-700 hover:underline">+ Add option</button>
        )}
        <label className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
          <Clock className="w-3.5 h-3.5 text-violet-600" /> Closes:
          <input type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)}
            min={new Date(Date.now() + 3600000).toISOString().slice(0, 16)} data-testid="poll-closes-input"
            title="Optional — non-voters get a reminder 24h before, then the poll closes itself"
            className="border border-zinc-300 px-2 py-1 text-xs focus:outline-none focus:border-violet-500" />
        </label>
        <button onClick={submit} disabled={busy} data-testid="poll-submit"
          className="ml-auto px-4 py-1.5 bg-violet-700 text-white text-xs font-700 hover:bg-violet-600 disabled:opacity-50">
          {busy ? "Posting…" : "Post poll"}
        </button>
      </div>
      {closesAt && <p className="text-[10px] text-muted-foreground mt-1">Members who haven't voted get a reminder ~24h before closing; the poll closes itself and announces the winner.</p>}
    </div>
  );
}

function PollCard({ tripId, poll, isOwner, onChange }) {
  const [busy, setBusy] = useState(false);
  const closed = poll.status !== "open";
  const vote = async (i) => {
    if (closed || busy) return;
    setBusy(true);
    try { await api.post(`/trips/${tripId}/polls/${poll.id}/vote`, { option: i }); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Vote failed"); }
    setBusy(false);
  };
  const close = async () => { try { await api.post(`/trips/${tripId}/polls/${poll.id}/close`); onChange(); } catch { toast.error("Could not close poll"); } };
  const del = async () => { try { await api.delete(`/trips/${tripId}/polls/${poll.id}`); toast.success("Poll deleted"); onChange(); } catch { toast.error("Could not delete poll"); } };
  const max = Math.max(1, ...poll.counts);
  return (
    <div className="border border-zinc-300 bg-white p-4" data-testid={`poll-${poll.id}`}>
      <div className="flex items-start gap-2">
        <div className="font-700 text-sm flex-1">{poll.question}</div>
        {!closed && poll.closes_at && (
          <span className="inline-flex items-center gap-1 text-[10px] font-700 bg-violet-50 text-violet-700 px-1.5 py-0.5 border border-violet-200" data-testid={`poll-closes-${poll.id}`}
            title="Non-voters get a reminder 24h before; the poll closes itself at this time">
            <Clock className="w-3 h-3" /> closes {new Date(poll.closes_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
          </span>
        )}
        {closed && <span className="inline-flex items-center gap-1 text-[10px] font-800 uppercase bg-zinc-100 text-zinc-600 px-1.5 py-0.5 border border-zinc-300"><Lock className="w-3 h-3" /> Closed</span>}
        {(isOwner || false) && !closed && (
          <button onClick={close} data-testid={`poll-close-${poll.id}`} title="Close voting"
            className="text-[11px] font-700 text-zinc-500 hover:text-violet-700">Close</button>
        )}
        {isOwner && (
          <button onClick={del} data-testid={`poll-delete-${poll.id}`} title="Delete poll" className="text-zinc-400 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
      <div className="mt-2.5 space-y-1.5">
        {poll.options.map((opt, i) => {
          const n = poll.counts[i];
          const mine = poll.my_vote === i;
          return (
            <button key={i} onClick={() => vote(i)} disabled={closed || busy} data-testid={`poll-vote-${poll.id}-${i}`}
              className={`relative w-full text-left border px-3 py-2 text-sm overflow-hidden transition-colors ${mine ? "border-violet-500" : "border-zinc-300 hover:border-violet-400"} ${closed ? "cursor-default" : "cursor-pointer"}`}
              title={poll.voter_names?.[i]?.join(", ") || ""}>
              <span className="absolute inset-y-0 left-0 bg-violet-100" style={{ width: `${(n / max) * 100}%` }} />
              <span className="relative flex items-center gap-2">
                {mine && <Check className="w-3.5 h-3.5 text-violet-700 shrink-0" />}
                <span className={mine ? "font-700 text-violet-900" : ""}>{opt}</span>
                <span className="ml-auto text-xs font-mono text-muted-foreground">{n}</span>
              </span>
            </button>
          );
        })}
      </div>
      <div className="mt-1.5 text-[11px] text-muted-foreground">
        {poll.total_votes} vote{poll.total_votes === 1 ? "" : "s"} · by {poll.created_by}{!closed && " · tap an option to vote (you can change it)"}
      </div>
    </div>
  );
}
